import type { TaxonomyLevelMetadata } from './taxonomyTree';

export const METRO_AREA_TAXONOMY_LABEL = 'Metropolitan areas';
export const STATE_COUNTY_TAXONOMY_LABEL = 'States and counties';

/**
 * Display names for the geography taxonomies.
 *
 * `taxonomyList(GEOGRAPHY)` returns machine slugs built from the level tags
 * (`region_country_state_county`), which are not presentable. Keyed by slug
 * rather than id so a taxonomy keeps its name if ids ever shift.
 */
const GEOGRAPHY_TAXONOMY_LABEL_BY_NAME: Record<string, string> = {
  region_country_metro_area_city_core: METRO_AREA_TAXONOMY_LABEL,
  region_country_state_county: STATE_COUNTY_TAXONOMY_LABEL,
};

/** Turns a level tag into title case: `metro_area` → `Metro Area`. */
const humanizeSlug = (slug: string): string =>
  slug
    .split('_')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

/**
 * A presentable name for a taxonomy.
 *
 * The map covers the taxonomies we know. For anything else, prefer the
 * taxonomy's own level chain over humanising the slug: the backend has renamed
 * these tags twice, and a humanised slug ("Region Country State County") still
 * reads as raw machine text to a user. Passing `levels` is optional only
 * because the tree may not have landed yet.
 */
export const getGeographyTaxonomyLabel = (
  name: string,
  levels: readonly TaxonomyLevelMetadata[] = []
): string =>
  GEOGRAPHY_TAXONOMY_LABEL_BY_NAME[name] ||
  getGeographyLevelChain(levels) ||
  humanizeSlug(name);

const sortedLevels = (levels: readonly TaxonomyLevelMetadata[]) =>
  [...levels].sort((a, b) => a.id - b.id);

/**
 * Level tags whose backend label is not what the product calls that level.
 *
 * `city_core` is the backend's name for the level we have always shown as a
 * city — the metro taxonomy reads "Region › Country › Metro Area › City", not
 * "City Core". Keyed by tag rather than label so a label rename upstream
 * doesn't quietly reintroduce the internal wording.
 */
const GEOGRAPHY_LEVEL_LABEL_BY_TAG: Record<string, string> = {
  city_core: 'City',
};

const levelText = (level: TaxonomyLevelMetadata): string => {
  const tag = level.tag ?? '';
  return GEOGRAPHY_LEVEL_LABEL_BY_TAG[tag] ?? humanizeSlug(level.label || tag);
};

/**
 * The taxonomy's levels as tree branch headings, shallowest first.
 *
 * The generic `getTaxonomyBranchLabels` takes the backend label verbatim, which
 * is right for role and skill but shows "City Core" here. Geography routes
 * through this instead so the tree panels agree with the picker.
 */
export const getGeographyBranchLabels = (
  levels: readonly TaxonomyLevelMetadata[]
): Array<{ label: string }> =>
  sortedLevels(levels).map((level) => ({ label: levelText(level) }));

/**
 * A single level's label — "State", "Metro Area". Used for the tree's own
 * branch heading, which names the level the panel is showing rather than the
 * taxonomy it belongs to.
 */
export const getGeographyLevelLabel = (
  levels: readonly TaxonomyLevelMetadata[],
  depth: number
): string => {
  const level = sortedLevels(levels)[depth];
  return level ? levelText(level) : '';
};

/**
 * The taxonomy's levels as a readable chain — "Region › Country › State ›
 * County". `fromDepth` drops the levels above it, so a tree panel can describe
 * just what remains below the level being shown.
 */
export const getGeographyLevelChain = (
  levels: readonly TaxonomyLevelMetadata[],
  fromDepth = 0
): string =>
  sortedLevels(levels)
    .slice(fromDepth)
    .map(levelText)
    .filter(Boolean)
    .join(' › ');
